import React, { useState, useEffect } from 'react';
import { Users, FolderOpen, FileText, Plus, Edit2, Trash2, Search, RefreshCw, Database, CheckCircle, AlertCircle } from 'lucide-react';
import { API_CONFIG } from '../config/api';

const API_URL = API_CONFIG.API_URL.replace('/api', '/api/crud');

interface User {
  id: number;
  name: string;
  email: string;
  created_at: string;
  updated_at: string;
}

interface Project {
  id: number;
  name: string;
  description: string;
  user_id: number;
  status: 'draft' | 'active' | 'completed' | 'archived';
  config: any;
  created_at: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
}

interface File {
  id: number;
  filename: string;
  original_name: string;
  mime_type: string;
  size: number;
  project_id: number;
  user_id: number;
  storage_path: string;
  created_at: string;
  project_name?: string;
  user_name?: string;
}

interface Stats {
  users: number;
  projects: number;
  files: number;
  projectsByStatus: Record<string, number>;
}

export const CrudDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'projects' | 'files' | 'stats'>('stats');
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states
  const [userForm, setUserForm] = useState({ name: '', email: '' });
  const [projectForm, setProjectForm] = useState({ name: '', description: '', user_id: 1, status: 'draft' as const });
  const [fileForm, setFileForm] = useState({ 
    filename: '', 
    original_name: '', 
    mime_type: 'application/pdf', 
    size: 1024, 
    project_id: 1, 
    user_id: 1, 
    storage_path: '' 
  });
  
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingProject, setEditingProject] = useState<Project | null>(null);

  // Fetch data functions
  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/stats`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/users`);
      const data = await res.json();
      setUsers(data.data || []);
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/projects`);
      const data = await res.json();
      setProjects(data.data || []);
    } catch (err) {
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/files`);
      const data = await res.json();
      setFiles(data.data || []);
    } catch (err) {
      setError('Failed to fetch files');
    } finally {
      setLoading(false);
    }
  };

  // CRUD operations
  const createUser = async () => {
    try {
      const res = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userForm)
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create user');
      }
      
      setSuccess('User created successfully!');
      setUserForm({ name: '', email: '' });
      fetchUsers();
      fetchStats();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const updateUser = async () => {
    if (!editingUser) return;
    
    try {
      const res = await fetch(`${API_URL}/users/${editingUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editingUser.name, email: editingUser.email })
      });
      
      if (!res.ok) throw new Error('Failed to update user');
      
      setSuccess('User updated successfully!');
      setEditingUser(null);
      fetchUsers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const deleteUser = async (id: number) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const res = await fetch(`${API_URL}/users/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete user');
      
      setSuccess('User deleted successfully!');
      fetchUsers();
      fetchStats();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const createProject = async () => {
    try {
      const res = await fetch(`${API_URL}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...projectForm,
          config: { model: 'gpt-4', features: ['chat'] }
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create project');
      }
      
      setSuccess('Project created successfully!');
      setProjectForm({ name: '', description: '', user_id: 1, status: 'draft' });
      fetchProjects();
      fetchStats();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const updateProject = async () => {
    if (!editingProject) return;
    
    try {
      const res = await fetch(`${API_URL}/projects/${editingProject.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name: editingProject.name, 
          description: editingProject.description,
          status: editingProject.status 
        })
      });
      
      if (!res.ok) throw new Error('Failed to update project');
      
      setSuccess('Project updated successfully!');
      setEditingProject(null);
      fetchProjects();
      fetchStats();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const deleteProject = async (id: number) => {
    if (!confirm('Are you sure you want to delete this project?')) return;
    
    try {
      const res = await fetch(`${API_URL}/projects/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete project');
      
      setSuccess('Project deleted successfully!');
      fetchProjects();
      fetchStats();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const createFile = async () => {
    try {
      const res = await fetch(`${API_URL}/files`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...fileForm,
          storage_path: fileForm.storage_path || `files/${new Date().getFullYear()}/${fileForm.filename}`
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create file');
      }
      
      setSuccess('File record created successfully!');
      setFileForm({ 
        filename: '', 
        original_name: '', 
        mime_type: 'application/pdf', 
        size: 1024, 
        project_id: 1, 
        user_id: 1, 
        storage_path: '' 
      });
      fetchFiles();
      fetchStats();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const deleteFile = async (id: number) => {
    if (!confirm('Are you sure you want to delete this file?')) return;
    
    try {
      const res = await fetch(`${API_URL}/files/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete file');
      
      setSuccess('File deleted successfully!');
      fetchFiles();
      fetchStats();
    } catch (err: any) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchStats();
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'projects') fetchProjects();
    if (activeTab === 'files') fetchFiles();
  }, [activeTab]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-8">
          <h1 className="text-4xl font-black text-white mb-2 flex items-center gap-3">
            <Database className="w-10 h-10 text-yellow-400" />
            CRUD Dashboard
          </h1>
          <p className="text-gray-300">Manage your D1 database with full CRUD operations</p>
        </div>

        {/* Notifications */}
        {success && (
          <div className="bg-green-500/20 border-2 border-green-500 text-green-300 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            {success}
          </div>
        )}
        
        {error && (
          <div className="bg-red-500/20 border-2 border-red-500 text-red-300 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'stats', label: 'Statistics', icon: Database },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'projects', label: 'Projects', icon: FolderOpen },
            { id: 'files', label: 'Files', icon: FileText }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-purple-900'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Statistics Tab */}
        {activeTab === 'stats' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
              <Users className="w-8 h-8 text-blue-400 mb-2" />
              <div className="text-3xl font-bold text-white">{stats.users}</div>
              <div className="text-gray-300">Total Users</div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
              <FolderOpen className="w-8 h-8 text-green-400 mb-2" />
              <div className="text-3xl font-bold text-white">{stats.projects}</div>
              <div className="text-gray-300">Total Projects</div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
              <FileText className="w-8 h-8 text-yellow-400 mb-2" />
              <div className="text-3xl font-bold text-white">{stats.files}</div>
              <div className="text-gray-300">Total Files</div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
              <Database className="w-8 h-8 text-purple-400 mb-2" />
              <div className="text-sm text-gray-300">
                {Object.entries(stats.projectsByStatus).map(([status, count]) => (
                  <div key={status} className="flex justify-between">
                    <span className="capitalize">{status}:</span>
                    <span className="font-bold text-white">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6">
            {/* Create User Form */}
            <div className="mb-6 p-4 bg-white/10 rounded-lg">
              <h3 className="text-xl font-bold text-white mb-3">Create New User</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Name"
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  className="flex-1 px-4 py-2 bg-white/20 text-white placeholder-gray-400 rounded-lg"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  className="flex-1 px-4 py-2 bg-white/20 text-white placeholder-gray-400 rounded-lg"
                />
                <button
                  onClick={createUser}
                  className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create
                </button>
              </div>
            </div>

            {/* Users List */}
            <div className="space-y-2">
              {users.map(user => (
                <div key={user.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10">
                  {editingUser?.id === user.id ? (
                    <>
                      <input
                        type="text"
                        value={editingUser.name}
                        onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                        className="px-3 py-1 bg-white/20 text-white rounded mr-2"
                      />
                      <input
                        type="email"
                        value={editingUser.email}
                        onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                        className="px-3 py-1 bg-white/20 text-white rounded mr-2"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={updateUser}
                          className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingUser(null)}
                          className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <div className="text-white font-semibold">{user.name}</div>
                        <div className="text-gray-400 text-sm">{user.email}</div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingUser(user)}
                          className="p-2 text-blue-400 hover:bg-white/10 rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteUser(user.id)}
                          className="p-2 text-red-400 hover:bg-white/10 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6">
            {/* Create Project Form */}
            <div className="mb-6 p-4 bg-white/10 rounded-lg">
              <h3 className="text-xl font-bold text-white mb-3">Create New Project</h3>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Project Name"
                  value={projectForm.name}
                  onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                  className="px-4 py-2 bg-white/20 text-white placeholder-gray-400 rounded-lg"
                />
                <select
                  value={projectForm.status}
                  onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value as any })}
                  className="px-4 py-2 bg-white/20 text-white rounded-lg"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Description"
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                  className="flex-1 px-4 py-2 bg-white/20 text-white placeholder-gray-400 rounded-lg"
                />
                <input
                  type="number"
                  placeholder="User ID"
                  value={projectForm.user_id}
                  onChange={(e) => setProjectForm({ ...projectForm, user_id: parseInt(e.target.value) })}
                  className="w-24 px-4 py-2 bg-white/20 text-white placeholder-gray-400 rounded-lg"
                />
                <button
                  onClick={createProject}
                  className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create
                </button>
              </div>
            </div>

            {/* Projects List */}
            <div className="space-y-2">
              {projects.map(project => (
                <div key={project.id} className="p-4 bg-white/5 rounded-lg hover:bg-white/10">
                  {editingProject?.id === project.id ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editingProject.name}
                        onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                        className="w-full px-3 py-1 bg-white/20 text-white rounded"
                      />
                      <input
                        type="text"
                        value={editingProject.description}
                        onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                        className="w-full px-3 py-1 bg-white/20 text-white rounded"
                      />
                      <select
                        value={editingProject.status}
                        onChange={(e) => setEditingProject({ ...editingProject, status: e.target.value as any })}
                        className="px-3 py-1 bg-white/20 text-white rounded"
                      >
                        <option value="draft">Draft</option>
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                        <option value="archived">Archived</option>
                      </select>
                      <div className="flex gap-2">
                        <button
                          onClick={updateProject}
                          className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingProject(null)}
                          className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white font-semibold">{project.name}</div>
                        <div className="text-gray-400 text-sm">{project.description}</div>
                        <div className="flex gap-4 mt-1">
                          <span className={`text-xs px-2 py-1 rounded ${
                            project.status === 'active' ? 'bg-green-500/20 text-green-400' :
                            project.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                            project.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {project.status}
                          </span>
                          <span className="text-xs text-gray-500">
                            by {project.user_name}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingProject(project)}
                          className="p-2 text-blue-400 hover:bg-white/10 rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteProject(project.id)}
                          className="p-2 text-red-400 hover:bg-white/10 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Files Tab */}
        {activeTab === 'files' && (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6">
            {/* Create File Form */}
            <div className="mb-6 p-4 bg-white/10 rounded-lg">
              <h3 className="text-xl font-bold text-white mb-3">Create File Record</h3>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <input
                  type="text"
                  placeholder="Filename"
                  value={fileForm.filename}
                  onChange={(e) => setFileForm({ ...fileForm, filename: e.target.value })}
                  className="px-4 py-2 bg-white/20 text-white placeholder-gray-400 rounded-lg"
                />
                <input
                  type="text"
                  placeholder="Original Name"
                  value={fileForm.original_name}
                  onChange={(e) => setFileForm({ ...fileForm, original_name: e.target.value })}
                  className="px-4 py-2 bg-white/20 text-white placeholder-gray-400 rounded-lg"
                />
              </div>
              <div className="flex gap-3">
                <input
                  type="number"
                  placeholder="Project ID"
                  value={fileForm.project_id}
                  onChange={(e) => setFileForm({ ...fileForm, project_id: parseInt(e.target.value) })}
                  className="w-32 px-4 py-2 bg-white/20 text-white placeholder-gray-400 rounded-lg"
                />
                <input
                  type="number"
                  placeholder="User ID"
                  value={fileForm.user_id}
                  onChange={(e) => setFileForm({ ...fileForm, user_id: parseInt(e.target.value) })}
                  className="w-32 px-4 py-2 bg-white/20 text-white placeholder-gray-400 rounded-lg"
                />
                <input
                  type="number"
                  placeholder="Size (bytes)"
                  value={fileForm.size}
                  onChange={(e) => setFileForm({ ...fileForm, size: parseInt(e.target.value) })}
                  className="w-32 px-4 py-2 bg-white/20 text-white placeholder-gray-400 rounded-lg"
                />
                <button
                  onClick={createFile}
                  className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create
                </button>
              </div>
            </div>

            {/* Files List */}
            <div className="space-y-2">
              {files.map(file => (
                <div key={file.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10">
                  <div>
                    <div className="text-white font-semibold">{file.original_name}</div>
                    <div className="text-gray-400 text-sm">
                      {file.filename} • {(file.size / 1024).toFixed(1)} KB • {file.mime_type}
                    </div>
                    <div className="text-gray-500 text-xs mt-1">
                      Project: {file.project_name} • User: {file.user_name}
                    </div>
                  </div>
                  <button
                    onClick={() => deleteFile(file.id)}
                    className="p-2 text-red-400 hover:bg-white/10 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};